<template>
  <div
    ref="contentTag"
    v-tooltip="{
      content: showTooltip && tooltipText,
      distance: 10,
      delay: { show: 300, hide: 100 },
    }"
    :class="[
      'content-tag',
      tagType,
      {
        'is-active': showActiveState && active,
        'active-enabled': showActiveState,
        truncate,
        disabled,
      },
    ]"
    :data-external="externalId"
    @click="!disabled && $emit('click')"
    @mouseover.stop="handleHover(true)"
    @mouseleave.stop="handleHover(false)"
  >
    <span v-if="$slots.leftIcon && showIconSlots" class="tag-icon left">
      <slot name="leftIcon" />
    </span>
    <slot />
    <span v-if="$slots.right && showIconSlots" class="tag-icon right">
      <slot name="right" />
    </span>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { ContentTagType } from '../types/enums/content-tag-type';

const {
  tagType = ContentTagType.Primary,
  showActiveState = true,
  active,
  externalId,
  disabled,
  truncate,
} = defineProps<{
  showActiveState?: boolean;
  tagType?: ContentTagType;
  externalId: string;
  active?: boolean;
  truncate?: boolean;
  disabled?: boolean;
}>();

const emit = defineEmits<{
  hover: [boolean];
  click: [];
}>();

const isHovering = ref(false);

const showIconSlots = computed(() => isHovering.value || active);

const handleHover = (value: boolean) => {
  if (disabled) return;
  isHovering.value = value;
  emit('hover', value);
};

const contentTag = ref<HTMLElement>(null);

const showTooltip = computed(() => {
  if (!contentTag.value) return false;
  return truncate && contentTag.value.offsetWidth >= 200;
});

const tooltipText = computed(() => contentTag.value?.innerText);
</script>

<style lang="scss" scoped>
$icon-size: 1rem;

.content-tag {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  padding: var(--spacing-25) var(--spacing-75);
  border-radius: 32px;
  width: fit-content;
  max-width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-25);
  background: var(--gray-96);
  transition: all 0.15s ease-in-out;
  cursor: pointer;

  &.disabled {
    cursor: default;
  }

  .tag-icon {
    width: $icon-size;
    height: $icon-size;

    :deep(svg) {
      width: 100%;
      height: 100%;
      max-height: $icon-size;
    }

    &.left {
      margin-right: var(--spacing-50);
    }

    &.right {
      margin-left: var(--spacing-50);
    }
  }

  &.primary {
    background-color: var(--gray-96);
    border: 1px solid var(--gray-96);

    &:not(.disabled) {
      &:not(.is-active) {
        &:hover,
        &:focus {
          background-color: var(--gray-94);
          border-color: var(--gray-94);
        }
      }
    }
  }

  &.secondary {
    background-color: var(--white);
    border: 1px solid var(--base-blue);

    &.disabled {
      border-color: var(--gray-60);
      color: var(--gray-60);
    }

    &:not(.disabled) {
      &:hover,
      &:focus {
        background-color: var(--gray-96);
      }
    }
  }

  &.is-active {
    background-color: var(--blue-95);
    border: 1px solid var(--blue-45);
  }
}

.truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
  display: block;
}
</style>
